import React, { useState, useEffect } from "react";
import {
  Box,
  TextField,
  Button,
  CircularProgress,
  MenuItem,
} from "@mui/material";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { RootState, AppDispatch } from "../../../../redux/store";
import { upsertShortGuestList } from "../../../../redux/shortstayReservationSlice";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";

import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import countries from "../../../../GLOBAL/Countries";
import { ArrowLeft } from "lucide-react";

const ShortGuestList: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();

  const { reservationId, listingId, guestCount } = location.state || {};
  const listings = useSelector((state: RootState) => state.shortstays.listings);
  const user = useSelector((state: RootState) => state.auth.user);

  const listing = listings.find((l) => l.listingId === listingId);

  const [guests, setGuests] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [saved, setSaved] = useState(false);

  // -----------------------------
  // Initialize Guests
  // -----------------------------
  useEffect(() => {
    if (!reservationId || !listingId) {
      navigate(`/shortbookings/${userId}`);
      return;
    }

    // Guest 1 (You)
    const firstGuest = {
      fullName: `${user?.firstName || ""} ${user?.lastName || ""}`.trim(),
      emailAddress: user?.emailAddress || user?.email || "",
      contactNumber: user?.phoneNumber || "",
      passportId: "",
      country: "",
      dateOfBirth: null,
      isPrimary: true,
    };

    // Other guests
    const others = Array.from({ length: Math.max((guestCount || 1) - 1, 0) }).map(() => ({
      fullName: "",
      contactNumber: "",
      emailAddress: "",
      passportId: "",
      country: "",
      dateOfBirth: null,
      isPrimary: false,
    }));

    setGuests([firstGuest, ...others]);
  }, [user, reservationId, listingId, guestCount, navigate, userId]);

  // -----------------------------
  // Helpers
  // -----------------------------
  const handleChange = (index: number, key: string, value: any) => {
    const updated = [...guests];
    updated[index][key] = value;
    setGuests(updated);
  };

  const getAge = (dob: Date | null) => {
    if (!dob) return 0;
    const diff = Date.now() - dob.getTime();
    return Math.abs(new Date(diff).getUTCFullYear() - 1970);
  };

  const isFormValid = guests.every((g) => {
    if (g.isPrimary) return true;
    const age = getAge(g.dateOfBirth);

    const allFilled =
      g.fullName.trim() !== "" &&
      g.passportId.trim() !== "" &&
      g.country.trim() !== "" &&
      g.dateOfBirth !== null &&
      (g.contactNumber.trim() !== "" || g.emailAddress.trim() !== "");

    if (!allFilled) return false;

    // under 18 can reuse main user's email
    if (age < 18) return true;

    // adult cannot reuse user's email
    if (age >= 18 && g.emailAddress === user?.emailAddress) return false;

    return true;
  });

  // -----------------------------
  // Save Handler
  // -----------------------------
  const handleSave = async () => {
    if (!isFormValid) return;
    setLoading(true);

    try {
      await dispatch(
        upsertShortGuestList({
          reservationId,
          userId: user?.userId || "",
          listingId,
          guests: guests.map((g) => ({
            ...g,
            dateOfBirth: g.dateOfBirth ? g.dateOfBirth.toISOString() : null,
          })),
        })
      );

      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
      navigate(`/shortbookings/${userId}`);
    } catch (err) {
      console.error("❌ Error saving guest list:", err);
    } finally {
      setLoading(false);
    }
  };

  // -----------------------------
  // RENDER
  // -----------------------------
  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "#f7f7f7",
        pb: 10,
        fontFamily: "Poppins, sans-serif",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          background: "#fff",
          p: 1.5,
          display: "flex",
          alignItems: "center",
          gap: 1,
          boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
          zIndex: 10,
        }}
      >
        <Button onClick={() => navigate(-1)} sx={{ color: "#111" }}>
          <ArrowLeft size={20} />
        </Button>
        <h3 style={{ margin: 0, fontWeight: 600 }}>Guest Details</h3>
      </Box>

      {/* Listing Preview */}
      {listing && (
        <Box
          sx={{
            mt: 9,
            mx: 2,
            display: "flex",
            alignItems: "center",
            gap: 2,
            background: "#fff",
            borderRadius: 3,
            p: 2,
            boxShadow: "0 4px 10px rgba(0,0,0,0.1)",
          }}
        >
          <img
            src={listing.photos?.[0] || "/default-shortstay.jpg"}
            alt="Listing"
            style={{
              width: 90,
              height: 90,
              borderRadius: 10,
              objectFit: "cover",
            }}
          />
          <div>
            <h4 style={{ margin: "0 0 4px 0", fontWeight: 600 }}>
              {listing.title || "Stay"}
            </h4>
            <p style={{ margin: 0, color: "#555", fontSize: "0.9rem" }}>
              {listing.address?.city}, {listing.address?.country}
            </p>
          </div>
        </Box>
      )}

      {/* Info Banner */}
      <Box
        sx={{
          mt: 3,
          mx: 2,
          background: "#fffbe6",
          border: "1px solid #ffe58f",
          borderRadius: 2,
          p: 1.5,
          color: "#614700",
          fontSize: "0.9rem",
        }}
      >
        ⚠️ Guests under 18 may use your email or contact information.
      </Box>

      {/* Guest Forms */}
      <Box sx={{ mt: 3, mx: 2, display: "flex", flexDirection: "column", gap: 3 }}>
        {guests.map((g, i) => {
          const age = getAge(g.dateOfBirth);
          const isAdult = age >= 18;

          return (
            <Box
              key={i}
              sx={{
                background: "#fff",
                p: 2.5,
                borderRadius: 3,
                boxShadow: "0 4px 10px rgba(0,0,0,0.1)",
              }}
            >
              <h4 style={{ marginBottom: 12, fontWeight: 600 }}>
                {g.isPrimary ? "Guest 1 (You)" : `Guest ${i + 1}`}
              </h4>

              {g.isPrimary ? (
                <>
                  <TextField
                    fullWidth
                    label="Full Name"
                    value={g.fullName}
                    InputLabelProps={{ shrink: true }}
                    sx={readonlyField}
                    InputProps={{ readOnly: true }}
                  />
                  <TextField
                    fullWidth
                    label="Email Address"
                    value={g.emailAddress}
                    InputLabelProps={{ shrink: true }}
                    sx={readonlyField}
                    InputProps={{ readOnly: true }}
                  />
                  <TextField
                    fullWidth
                    label="Phone Number"
                    value={g.contactNumber}
                    InputLabelProps={{ shrink: true }}
                    sx={readonlyField}
                    InputProps={{ readOnly: true }}
                  />
                </>
              ) : (
                <>
                  <TextField
                    fullWidth
                    label="Full Name (as per ID)"
                    value={g.fullName}
                    onChange={(e) => handleChange(i, "fullName", e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={fieldStyle}
                  />

                  <TextField
                    fullWidth
                    label={
                      age < 18
                        ? "Contact Number (optional if using guardian’s email)"
                        : "Contact Number e.g. +254 700 000 000"
                    }
                    placeholder={
                      age < 18
                        ? user?.phoneNumber ||
                          "You can skip if using guardian’s contact"
                        : "+254 700 000 000"
                    }
                    value={g.contactNumber}
                    onChange={(e) =>
                      handleChange(i, "contactNumber", e.target.value)
                    }
                    InputLabelProps={{ shrink: true }}
                    sx={fieldStyle}
                  />

                  <TextField
                    fullWidth
                    label={
                      age < 18
                        ? "Email Address (You can use your guardian’s email)"
                        : "Email Address"
                    }
                    placeholder={
                      age < 18
                        ? user?.emailAddress ||
                          "Use guardian’s email (e.g. your@example.com)"
                        : "Enter guest's email address"
                    }
                    value={g.emailAddress}
                    onChange={(e) =>
                      handleChange(i, "emailAddress", e.target.value)
                    }
                    InputLabelProps={{ shrink: true }}
                    sx={fieldStyle}
                    error={isAdult && g.emailAddress === user?.emailAddress}
                    helperText={
                      isAdult && g.emailAddress === user?.emailAddress
                        ? "Adults must use their own email."
                        : ""
                    }
                  />

                  <TextField
                    fullWidth
                    label="Passport / ID Number"
                    value={g.passportId}
                    onChange={(e) =>
                      handleChange(i, "passportId", e.target.value)
                    }
                    InputLabelProps={{ shrink: true }}
                    sx={fieldStyle}
                  />

                  {/* Country Dropdown */}
                  <TextField
                    select
                    fullWidth
                    label="Nationality / Country"
                    value={g.country || ""}
                    onChange={(e) => handleChange(i, "country", e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={fieldStyle}
                  >
                    {countries.map((c) => (
                      <MenuItem key={c.code} value={c.name}>
                        {c.emoji} {c.name}
                      </MenuItem>
                    ))}
                  </TextField>

                  {/* Date of Birth */}
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DatePicker
                      label="Date of Birth"
                      value={g.dateOfBirth}
                      onChange={(newDate) =>
                        handleChange(i, "dateOfBirth", newDate)
                      }
                      disableFuture
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          InputLabelProps: { shrink: true },
                          sx: fieldStyle,
                        },
                      }}
                    />
                  </LocalizationProvider>
                </>
              )}
            </Box>
          );
        })}
      </Box>

      {/* Save Button */}
      <Box sx={{ mx: 2, mt: 4 }}>
        <Button
          onClick={handleSave}
          fullWidth
          disabled={!isFormValid || loading}
          sx={{
            py: 1.6,
            borderRadius: 2,
            background: !isFormValid ? "#ccc" : "#e50914",
            color: "#fff",
            fontWeight: 700,
            fontSize: "1rem",
            "&:hover": { background: !isFormValid ? "#ccc" : "#b0060f" },
          }}
        >
          {loading ? (
            <CircularProgress size={24} sx={{ color: "#fff" }} />
          ) : (
            "Save Guest Details"
          )}
        </Button>
      </Box>

      {/* Saved Overlay */}
      {saved && (
        <Box
          sx={{
            position: "fixed",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backdropFilter: "blur(5px)",
            zIndex: 1000,
          }}
        >
          <CheckCircleOutlineIcon sx={{ fontSize: 100, color: "limegreen" }} />
        </Box>
      )}
    </Box>
  );
};

export default ShortGuestList;

/* ---------- STYLES ---------- */
const fieldStyle = {
  mb: 2,
  "& .MuiOutlinedInput-root": {
    borderRadius: 2,
    background: "#fff",
    "& fieldset": { borderColor: "#ccc" },
    "&:hover fieldset": { borderColor: "#bf9b30" },
    "&.Mui-focused fieldset": { borderColor: "#bf9b30" },
  },
  "& label": { color: "#777", fontWeight: 500 },
};

const readonlyField = {
  mb: 2,
  "& .MuiOutlinedInput-root": {
    borderRadius: 2,
    background: "#f5f5f5",
    "& fieldset": { borderColor: "#ddd" },
  },
  "& label": { color: "#777", fontWeight: 500 },
};
